import { useState, useMemo } from 'react'
import NextLink from 'next/link'

import { Text } from '@wartek-id/text-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Button } from '@wartek-id/button-v1'
import type { ButtonProps } from '@wartek-id/button-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import { PerencanaanTable } from 'app/KinerjaGpkPage/components/PerencanaanTable'
import { GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS } from 'app/KinerjaGpkPage/constants/contents'
import { useApiGetPerencanaanList } from 'api/kinerja/gpk/perencanaan'
import { PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH } from 'app/KinerjaShared/constants/path'

import type { PerencanaanTableItem } from 'app/KinerjaGpkPage/components/PerencanaanTable'
import type { KinerjaStatusGpk } from 'types/kinerja-gpk'

type StatusFilterOption = OptionItem & {
  value: KinerjaStatusGpk | 'ALL'
}

const ALLOWED_TO_CHECK_STATES: Array<KinerjaStatusGpk> = [
  'WAITING_APPROVAL',
  'WAITING_APPROVAL_PPK',
  'REVISION_TK',
  'REVISION_PPK',
]

const STATUS_FILTER_OPTIONS: Array<StatusFilterOption> = [
  { value: 'ALL', name: 'Semua Status' },
  {
    value: 'WAITING_APPROVAL',
    name: GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS['WAITING_APPROVAL'],
  },
  {
    value: 'WAITING_APPROVAL_PPK',
    name: GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS['WAITING_APPROVAL_PPK'],
  },
  {
    value: 'REVISION_TK',
    name: GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS['REVISION_TK'],
  },
  {
    value: 'REVISION_PPK',
    name: GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS['REVISION_PPK'],
  },
]

const ACTION_BUTTON_PROPS: {
  [key in KinerjaStatusGpk]?: {
    label: string
    color: ButtonProps['color']
  }
} = {
  WAITING_APPROVAL: {
    label: 'Periksa',
    color: 'black',
  },
  WAITING_APPROVAL_PPK: {
    label: 'Cek',
    color: 'white',
  },
  REVISION_TK: {
    label: 'Cek',
    color: 'white',
  },
  REVISION_PPK: {
    label: 'Cek',
    color: 'white',
  },
  APPROVED: {
    label: 'Cek',
    color: 'white',
  },
}

export function PeriksaPerencanaan() {
  // -----------------------------------------
  // Handle status filter
  // -----------------------------------------
  const [selectedStatus, setSelectedStatus] = useState<StatusFilterOption>({
    value: 'ALL',
    name: 'Semua Status',
  })

  const emptyStateMessage = useMemo(() => {
    const hasSelectedStatus = selectedStatus.value !== 'ALL'
    if (!hasSelectedStatus) return 'Tidak ada data perencanaan'

    return `Tidak ada data perencanaan dengan status "${
      GPK_PEGAWAI_PERENCANAAAN_STATUS_LABELS[selectedStatus.value]
    }"`
  }, [selectedStatus.value])

  // -----------------------------------------
  // Get perencanaan list data
  // -----------------------------------------
  const { data: perencanaanListData, isFetching } = useApiGetPerencanaanList({
    params: {
      // To improve data fetching efficiency, we'll fetch all states at once and use it in other pages.
      // Filtering will be handled client-side.
      states: ['ALL'],
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
  })

  const perencanaanItems: Array<PerencanaanTableItem> = useMemo(() => {
    if (!Array.isArray(perencanaanListData?.data)) return []

    // Only show submitted but not approved perencanaan
    const allowedToCheckItems = perencanaanListData.data.filter((item) =>
      ALLOWED_TO_CHECK_STATES.includes(item.state)
    )

    return allowedToCheckItems.map((item) => ({
      id: item.id,
      name: item.nama,
      status: item.state,
      ptkId: item.ptkId,
    }))
  }, [perencanaanListData])

  return (
    <main className="w-full max-w-7xl mx-auto py-8 px-6 min-h-screen">
      <Header title="Daftar Perencanaan Kinerja" className="mb-10" />

      <section className="shadow-card rounded-lg bg-white border border-disabled">
        <div className="px-8 pt-6 pb-4">
          <Text variant="heading-lg" as="h3" className="mb-2">
            Periksa Perencanaan Guru Pendidikan Khusus
          </Text>
          <Text color="subdued">
            Anda dapat berikan rekomendasi persetujuan ke Pejabat Penilai
            Kinerja atau arahkan Guru Pendidikan Khusus untuk revisi.
          </Text>
        </div>
        <div className="p-4">
          <CustomSelect
            className="w-fit min-w-64"
            value={selectedStatus}
            options={STATUS_FILTER_OPTIONS}
            onChange={(option: StatusFilterOption) => setSelectedStatus(option)}
          />
        </div>
        <PerencanaanTable
          items={perencanaanItems}
          statusFilter={[selectedStatus.value]}
          emptyStateMessage={emptyStateMessage}
          isLoading={isFetching}
          renderAction={(item) => {
            const buttonProps = ACTION_BUTTON_PROPS[item.status]

            if (!buttonProps) return null

            return (
              <NextLink
                href={`${PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH}/perencanaan/${item.ptkId}`}
                passHref
              >
                <Button
                  color={buttonProps.color}
                  size="sm"
                  as="a"
                  type={undefined}
                >
                  {buttonProps.label}
                </Button>
              </NextLink>
            )
          }}
        />
      </section>
    </main>
  )
}
